/**
 * The seven-day horizon, shared by both week generators.
 *
 * The planner is anchored to a Monday and the generator drops days already
 * gone, so the size of a plan used to depend on the day it was asked for: seven
 * on a Monday, four on a Thursday, three on a Friday. Across 212 production
 * plans, 88 (41%) came in short that way, and only nine of those were
 * onboarding — the rest was regeneration hitting the same filter. So the rule
 * lives here rather than in either caller.
 */
import { generateDinnerOnlyPlan, type DinnerMVPConfig, type GeneratedDinner } from './weekGeneratorService';
import type { PersonalRecipeIndexEntry } from './personalRecipeIndex';
import type { RecipeMetadata } from '../redux/recipeMetadataSlice';
import { getWeekStartForDate, type PlannedMeal } from '../redux/mealPlanSlice';
import { addDays } from '../utils/localDate';
import { mealIdentityOf } from './mealIdentity';

import { HORIZON_DAYS } from './horizonDays';

export { HORIZON_DAYS };

export interface HorizonRange {
  /** First day planned: today when it falls inside the week, else the week start. */
  startsAt: string;
  /** Last day planned, inclusive — `startsAt` + six. */
  endsAt: string;
  /** The Monday after `weekStart`, where the spill lands. */
  nextWeekStart: string;
  /** True when the horizon runs past Sunday and a second bucket is written. */
  spills: boolean;
}

function withinWeek(weekStart: string, iso: string): boolean {
  return iso >= weekStart && iso < addDays(weekStart, 7);
}

/**
 * Where a plan for `weekStart` starts and ends.
 *
 * `allowSpill` is the one thing the two callers disagree on. Onboarding always
 * spills: the next week is certainly empty. The live planner must not — writing
 * a week the cook has already planned would replace it, so regeneration stops
 * at Sunday when there is already food on the other side.
 */
export function horizonRange(weekStart: string, todayIso: string, allowSpill = true): HorizonRange {
  const startsAt = withinWeek(weekStart, todayIso) ? todayIso : weekStart;
  const endsAt = addDays(startsAt, HORIZON_DAYS - 1);
  const nextWeekStart = addDays(weekStart, 7);
  return { startsAt, endsAt, nextWeekStart, spills: allowSpill && endsAt >= nextWeekStart };
}

/**
 * One generation pass over the whole horizon.
 *
 * `generateDinnerOnlySlots` only ever builds seven slots from a single week
 * start, so a horizon that crosses Sunday is two calls rather than a
 * generalised slot builder — which would mean touching `slotKey`'s day index
 * (`mi * 7 + di`), and that is load-bearing across mobile, desktop, the config
 * screen, leftovers and the generator. The second call inherits the first's
 * picks as a hard exclusion so the two weeks never repeat a dish.
 *
 * By id alone that was not true (#736): "Fish Tacos" on the Sunday and "Tacos
 * poisson" on the Monday are two ids and one dinner. The second call now sees
 * the first's picks as dated dishes too, so the same-meal rules run across the
 * seam exactly as they do inside one week — and it stops at the horizon's last
 * day instead of choosing dinners that are thrown away.
 */
export function generateHorizonDinners(
  weekStart: string,
  recipes: PersonalRecipeIndexEntry[],
  config: DinnerMVPConfig,
  metadata: Record<string, RecipeMetadata>,
  range: HorizonRange,
): GeneratedDinner[] {
  const head = generateDinnerOnlyPlan(
    weekStart,
    recipes,
    { ...config, todayOverride: range.startsAt },
    metadata,
  );
  if (!range.spills) return head;

  const used = [...(config.previouslyGeneratedRecipeIds ?? []), ...head.map((d) => d.recipe.id)];
  const sameMealHistory = [
    ...(config.sameMealHistory ?? []),
    ...head.map((d) => ({ date: d.slot.date, identity: mealIdentityOf(d.recipe) })),
  ];
  const tail = generateDinnerOnlyPlan(
    range.nextWeekStart,
    recipes,
    { ...config, previouslyGeneratedRecipeIds: used, sameMealHistory, untilDate: range.endsAt },
    metadata,
  ).filter((d) => d.slot.date <= range.endsAt);

  return [...head, ...tail];
}

/**
 * The meals of one generation, split into the week buckets they belong to.
 *
 * `setWeeklyPlan`'s reducer already re-buckets by date, but
 * `mealPlanSyncMiddleware` resolves a single week from `payload.weekStartDate`
 * — so a spilled plan written in one dispatch would live in Redux and never
 * reach Firestore, and would be gone on the next boot. One dispatch per bucket
 * is what makes the spill durable.
 *
 * Buckets come back in date order, so the landing week is always written first.
 */
export function groupMealsByWeek(meals: PlannedMeal[]): Array<[string, PlannedMeal[]]> {
  const byWeek = new Map<string, PlannedMeal[]>();
  for (const meal of meals) {
    const week = getWeekStartForDate(meal.scheduledFor);
    const bucket = byWeek.get(week);
    if (bucket) bucket.push(meal);
    else byWeek.set(week, [meal]);
  }
  return [...byWeek.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}
